import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { Download, FileText, Wallet, TrendingUp } from "lucide-react";

const euro = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));

export async function openPayslip(id) {
  try {
    const res = await api.get(`/hr/me/payslips/${id}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error("No se pudo abrir la nómina");
  }
}

export default function Nominas() {
  const { data: payslips, loading } = useFetch("/hr/me/payslips");

  if (loading || !payslips) {
    return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const withNet = payslips.filter((p) => p.net != null);
  const avgNet = withNet.length ? Math.round(withNet.slice(0, 5).reduce((a, p) => a + p.net, 0) / Math.min(5, withNet.length)) : null;

  const KPI = [
    { label: "Neto mensual medio", value: euro(avgNet), sub: "últimos meses", icon: TrendingUp, accent: "text-chart-4 bg-chart-4/12" },
    { label: "Recibos disponibles", value: payslips.length, sub: `${new Date().getFullYear()}`, icon: FileText, accent: "text-primary bg-primary/12" },
    { label: "Último bruto", value: euro(payslips[0]?.gross), sub: payslips[0]?.month_label || "—", icon: Wallet, accent: "text-gold bg-gold/15" },
  ];

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="nominas-page">
      <PageHeader title="Nóminas" subtitle="Consulta y descarga tus recibos de salario" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {KPI.map((k, i) => (
          <Card key={i} className="border-border shadow-none p-5" data-testid={`nom-kpi-${i}`}>
            <div className={`h-10 w-10 rounded-md flex items-center justify-center ${k.accent}`}><k.icon className="h-5 w-5" /></div>
            <div className="font-heading font-bold text-2xl tabular-nums mt-4">{k.value}</div>
            <div className="text-sm text-muted-foreground">{k.label}</div>
            <div className="text-xs text-muted-foreground/70 mt-1">{k.sub}</div>
          </Card>
        ))}
      </div>

      <Card className="border-border shadow-none divide-y divide-border overflow-hidden">
        <div className="p-5 pb-3"><h3 className="font-heading font-bold text-base">Historial de nóminas</h3></div>
        {payslips.length === 0 ? (
          <div className="py-14 text-center text-sm text-muted-foreground">Todavía no hay nóminas disponibles.</div>
        ) : payslips.map((p) => (
          <div key={p.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/40 transition-colors duration-200" data-testid={`payslip-${p.id}`}>
            <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-muted-foreground" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{p.month_label}</div>
              <div className="text-xs text-muted-foreground tabular-nums">Bruto {euro(p.gross)} · IRPF {euro(p.irpf)} · Neto {euro(p.net)}</div>
            </div>
            <span className="hidden sm:block font-heading font-bold text-sm tabular-nums">{euro(p.net)}</span>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openPayslip(p.id)} data-testid={`download-${p.id}`}>
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span>
            </Button>
          </div>
        ))}
      </Card>
    </div>
  );
}
